import React from 'react';
import { Form, Button } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';

const validationSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    amount: Yup.number().required('Amount is required').positive('Amount must be positive'),
    category: Yup.string().required('Category is required'),
    dueDate: Yup.date().required('Due date is required'),
    description: Yup.string(),
    recurring: Yup.boolean(),
    notifyEmail: Yup.boolean(),
    notifyDays: Yup.number().when(['notifyEmail'], {
        is: true,
        then: () => Yup.number()
            .required('Days before due date is required')
            .min(1, 'Must be at least 1 day')
            .max(30, 'Must be less than 30 days'),
        otherwise: () => Yup.number().nullable()
    }),
    status: Yup.string()
});

export const BillForm = ({ categories, onSubmit, onCancel, initialValues = null }) => {
    const defaultValues = {
        name: '',
        amount: '',
        category: '',
        dueDate: '',
        description: '',
        recurring: false,
        status: 'pending',
        emailDelivered: false,
        notifyEmail: false,
        notifyDays: 3, // Default 3 days before
        ...initialValues
    };

    return (
        <Formik
            initialValues={defaultValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
        >
            {({ handleSubmit, handleChange, values, touched, errors, isSubmitting }) => (
                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                            type="text"
                            name="name"
                            value={values.name}
                            onChange={handleChange}
                            isInvalid={touched.name && errors.name}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.name}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Category</Form.Label>
                        <Form.Select
                            name="category"
                            value={values.category}
                            onChange={handleChange}
                            isInvalid={touched.category && errors.category}
                        >
                            <option value="">Select Category</option>
                            {categories.Category.map((category) => (
                                <option key={Math.random()} value={category.Category}>
                                    {category.Category}
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            {errors.category}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Amount</Form.Label>
                        <Form.Control
                            type="number"
                            name="amount"
                            value={values.amount}
                            onChange={handleChange}
                            isInvalid={touched.amount && errors.amount}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.amount}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Due Date</Form.Label>
                        <Form.Control
                            type="date"
                            name="dueDate"
                            value={values.dueDate}
                            onChange={handleChange}
                            isInvalid={touched.dueDate && errors.dueDate}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.dueDate}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            name="description"
                            value={values.description}
                            onChange={handleChange}
                            rows={3}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            name="recurring"
                            label="Recurring Bill"
                            checked={values.recurring}
                            onChange={handleChange}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            name="notifyEmail"
                            label="Email Notification"
                            checked={values.notifyEmail}
                            onChange={handleChange}
                        />
                    </Form.Group>

                    {values.notifyEmail && (
                        <Form.Group className="mb-3">
                            <Form.Label>Days Before Due Date</Form.Label>
                            <Form.Control
                                type="number"
                                name="notifyDays"
                                value={values.notifyDays}
                                onChange={handleChange}
                                isInvalid={touched.notifyDays && errors.notifyDays}
                                min="1"
                                max="30"
                            />
                            <Form.Text className="text-muted">
                                You'll receive an email reminder before the due date
                            </Form.Text>
                            <Form.Control.Feedback type="invalid">
                                {errors.notifyDays}
                            </Form.Control.Feedback>
                        </Form.Group>
                    )}

                    <div className="d-flex justify-content-end gap-2">
                        <Button variant="secondary" onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? 'Saving...' : 'Save Bill'}
                        </Button>
                    </div>
                </Form>
            )}
        </Formik>
    );
}; 